import { useEffect, useMemo, useState } from "react";
import {
  Accessibility,
  Baby,
  Bookmark,
  BookmarkCheck,
  ChevronRight,
  Clock3,
  ExternalLink,
  Info,
  Navigation,
  RefreshCw,
  ShieldCheck,
  Share2,
  X,
} from "lucide-react";
import { fetchToiletPhotos } from "../api";
import { accessMeta, featureMeta, formatDateTime, formatDistance } from "../format";
import type { DataSource, EvaluatedToilet, FeatureKey, ToiletPhoto } from "../types";
import { useModalDialog } from "../useModalDialog";
import { StatusBadge } from "./StatusBadge";
import { ToiletPhotoGallery } from "./ToiletPhotoGallery";

export function ToiletDetail({
  item,
  sources,
  onClose,
  onNavigate,
  onReport,
  localPhotos = [],
  enableRemotePhotos = true,
  reportEnabled = true,
  reportIsSimulation = false,
  navigationEnabled = true,
  originReady = true,
  onRequestOrigin,
  navigationUnavailableReason,
  reportUnavailableReason,
  favorite = false,
  hasExactRoute = false,
  onToggleFavorite,
  onShare,
}: {
  item: EvaluatedToilet;
  sources: DataSource[];
  onClose: () => void;
  onNavigate: () => void;
  onReport: () => void;
  localPhotos?: ToiletPhoto[];
  enableRemotePhotos?: boolean;
  reportEnabled?: boolean;
  reportIsSimulation?: boolean;
  navigationEnabled?: boolean;
  originReady?: boolean;
  onRequestOrigin?: () => void;
  navigationUnavailableReason?: string;
  reportUnavailableReason?: string;
  favorite?: boolean;
  hasExactRoute?: boolean;
  onToggleFavorite?: () => void;
  onShare?: () => Promise<"shared" | "copied">;
}) {
  const dialogRef = useModalDialog<HTMLElement>(onClose);
  const [remotePhotos, setRemotePhotos] = useState<ToiletPhoto[]>([]);
  const [photoLoadState, setPhotoLoadState] = useState<
    "idle" | "loading" | "ready" | "error"
  >("idle");
  const [photoRequestRevision, setPhotoRequestRevision] = useState(0);
  const [shareMessage, setShareMessage] = useState<string>();

  useEffect(() => {
    if (!enableRemotePhotos) {
      setRemotePhotos([]);
      setPhotoLoadState("idle");
      return;
    }
    const controller = new AbortController();
    setRemotePhotos([]);
    setPhotoLoadState("loading");
    void fetchToiletPhotos(item.id, { signal: controller.signal })
      .then((photos) => {
        if (controller.signal.aborted) return;
        setRemotePhotos(photos);
        setPhotoLoadState("ready");
      })
      .catch(() => {
        if (!controller.signal.aborted) setPhotoLoadState("error");
      });
    return () => controller.abort();
  }, [enableRemotePhotos, item.id, photoRequestRevision]);

  const photos = useMemo(() => {
    const merged = new Map<string, ToiletPhoto>();
    [...localPhotos, ...remotePhotos].forEach((photo) => merged.set(photo.id, photo));
    return [...merged.values()];
  }, [localPhotos, remotePhotos]);

  const sourceMap = new Map(sources.map((source) => [source.id, source]));
  const routeBlockedByState =
    item.state === "likely_unavailable" ||
    item.state === "unavailable_confirmed";

  return (
    <div className="dialog-backdrop" onMouseDown={onClose}>
      <section
        ref={dialogRef}
        className="detail-drawer"
        role="dialog"
        aria-modal="true"
        aria-labelledby="detail-title"
        tabIndex={-1}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <div className="drawer-heading">
          <div>
            <StatusBadge state={item.state} />
            <h2 id="detail-title">{item.name}</h2>
            <p>{item.address}</p>
          </div>
          <div className="detail-heading-actions">
            {onToggleFavorite ? (
              <button
                className="icon-button"
                type="button"
                onClick={onToggleFavorite}
                aria-pressed={favorite}
                aria-label={favorite ? "保存から外す" : "この候補を保存"}
              >
                {favorite ? (
                  <BookmarkCheck aria-hidden="true" />
                ) : (
                  <Bookmark aria-hidden="true" />
                )}
              </button>
            ) : null}
            {onShare ? (
              <button
                className="icon-button"
                type="button"
                onClick={() => {
                  setShareMessage(undefined);
                  void onShare()
                    .then((result) =>
                      setShareMessage(
                        result === "copied"
                          ? "候補リンクをコピーしました"
                          : "候補を共有しました",
                      ),
                    )
                    .catch(() => setShareMessage("共有できませんでした"));
                }}
                aria-label="この候補を共有"
              >
                <Share2 aria-hidden="true" />
              </button>
            ) : null}
            <button
              className="icon-button"
              type="button"
              onClick={onClose}
              data-dialog-initial-focus
            >
              <X aria-hidden="true" />
              <span className="sr-only">詳細を閉じる</span>
            </button>
          </div>
        </div>

        {shareMessage ? (
          <p className="detail-share-message" role="status" aria-live="polite">
            {shareMessage}
          </p>
        ) : null}

        <div className="detail-facts">
          <span>
            {originReady ? (
              <Navigation aria-hidden="true" />
            ) : (
              <Info aria-hidden="true" />
            )}
            <b>
              {originReady
                ? hasExactRoute
                  ? `到着 約${item.walkingMinutes}分`
                  : `徒歩目安 ${item.walkingMinutes}分`
                : `エリア中心から${formatDistance(item.distanceMeters)}`}
            </b>
            <small>{originReady ? formatDistance(item.distanceMeters) : "現在地ではありません"}</small>
          </span>
          <span>
            <Clock3 aria-hidden="true" />
            <b>{item.openingLabel}</b>
            <small>{accessMeta[item.publicAccess]}</small>
          </span>
        </div>

        <div className="detail-actions">
          <button
            type="button"
            className="primary-button"
            onClick={originReady ? onNavigate : onRequestOrigin}
            disabled={
              routeBlockedByState ||
              (originReady && !navigationEnabled) ||
              (!originReady && !onRequestOrigin)
            }
          >
            {originReady ? (
              <Navigation aria-hidden="true" />
            ) : (
              <Info aria-hidden="true" />
            )}
            {originReady ? "徒歩ルートを表示" : "出発地を設定"}
          </button>
          <button
            type="button"
            className="secondary-button"
            onClick={onReport}
            disabled={!reportEnabled}
          >
            {reportIsSimulation ? "報告（実証）" : "利用状況を報告"}
          </button>
        </div>
        {(!originReady || !navigationEnabled || routeBlockedByState || !reportEnabled) && (
          <p className="detail-action-note" role="status">
            {routeBlockedByState
              ? "利用困難の情報があるため、案内を停止しています。"
              : !originReady
                ? "現在地または駅・施設・住所を出発地に設定すると、徒歩案内を開始できます。"
                : !navigationEnabled
                  ? navigationUnavailableReason ?? "徒歩経路の取得は現在利用できません。"
                  : reportUnavailableReason ?? "現場報告は現在、閲覧のみです。"}
          </p>
        )}

        <ToiletPhotoGallery photos={photos} />
        {photoLoadState === "loading" && localPhotos.length === 0 && (
          <p className="photo-load-status" role="status">
            掲載写真を確認しています…
          </p>
        )}
        {photoLoadState === "error" && (
          <div className="photo-load-error" role="alert">
            <span>掲載写真を取得できませんでした。</span>
            <button
              type="button"
              className="secondary-button"
              onClick={() => setPhotoRequestRevision((current) => current + 1)}
            >
              <RefreshCw aria-hidden="true" />
              写真を再読み込み
            </button>
          </div>
        )}

        <section className="detail-section">
          <h3>設備</h3>
          <div className="detail-features">
            {(Object.entries(item.features) as [FeatureKey, boolean | null][]).map(
              ([feature, value]) => (
                <span className={value === true ? "is-available" : "is-unknown"} key={feature}>
                  {feature === "wheelchair" ? (
                    <Accessibility aria-hidden="true" />
                  ) : feature === "baby_changing" ? (
                    <Baby aria-hidden="true" />
                  ) : (
                    <ShieldCheck aria-hidden="true" />
                  )}
                  {featureMeta[feature]}
                  <small>{value === true ? "あり" : value === false ? "なし" : "不明"}</small>
                </span>
              ),
            )}
          </div>
          {item.openingNote && <p className="inline-note">{item.openingNote}</p>}
        </section>

        <details className="evidence-details">
          <summary>
            <span>
              <Info aria-hidden="true" />
              この判断の根拠
            </span>
            <ChevronRight aria-hidden="true" />
          </summary>
          <ol className="evidence-list">
            {item.evidence.map((evidence) => {
              const source = sourceMap.get(evidence.sourceId);
              return (
                <li key={evidence.id}>
                  <span className={`evidence-kind kind-${evidence.kind}`}>
                    {evidence.kind === "simulation" ? "訓練" : "根拠"}
                  </span>
                  <div>
                    <b>{evidence.label}</b>
                    <p>{evidence.detail}</p>
                    {evidence.observedAt && (
                      <small>更新・観測：{formatDateTime(evidence.observedAt)}</small>
                    )}
                    {source && (
                      <a
                        href={source.sourceUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {source.publisher}・{source.datasetName}
                        <ExternalLink aria-hidden="true" />
                        <span className="external-link-note">外部サイト・新しいタブ</span>
                      </a>
                    )}
                    {evidence.limitation && <em>{evidence.limitation}</em>}
                  </div>
                </li>
              );
            })}
          </ol>
        </details>

      </section>
    </div>
  );
}
